import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useTriggerCatalogExport } from '../catalog/hooks/use-catalog-export';
import { useExportDownload } from '../hooks/use-export-download';
import { useCancelExport, useTriggerExport } from '../hooks/use-export-mutations';
import { useExportStatusStream } from '../hooks/use-export-status-stream';
import { useExportStore, type ExportSession } from '../store/export.store';
import type { ExportStatus, ExportTriggerRequest } from '../types/export.types';
import { isTerminalExportStatus } from '../types/export.types';

import { ExportProgressToast } from './export-progress-toast';

/**
 * Compute a 0–100 progress value from a status frame: product counts when
 * available, else chunk counts, 100 once COMPLETED. Runs before PlanChunks
 * populates `expectedProducts` (both zero → 0%).
 */
function computePercent(
  status: ExportStatus,
  written: number,
  expected: number,
  chunksWritten: number,
  chunksTotal: number,
): number {
  if (status === 'COMPLETED') return 100;
  if (expected > 0) return (written / expected) * 100;
  if (chunksTotal > 0) return (chunksWritten / chunksTotal) * 100;
  return 0;
}

/**
 * Headless per-export runner: opens the SSE status stream, mirrors each frame
 * into the store, and re-emits a single Sonner toast in place. On terminal it
 * surfaces Download (COMPLETED) / Retry (FAILED) and clears the session from
 * the store when the user dismisses the toast. Mounted once per active session
 * by `ExportProgressManager`, above the router, so it survives navigation.
 */
export function ExportRunner({ session }: { session: ExportSession }) {
  const updateExport = useExportStore((s) => s.updateExport);
  const endExport = useExportStore((s) => s.endExport);
  const cancelMutation = useCancelExport();
  const triggerMutation = useTriggerExport();
  const catalogTriggerMutation = useTriggerCatalogExport();
  const { download, downloadFile, isRefreshing } = useExportDownload();
  // Set when the SSE stream gives up without reaching a terminal state — we
  // surface a FAILED-style toast so a dead stream doesn't hang on a spinner.
  const [streamUnavailable, setStreamUnavailable] = useState(false);
  // Flipped once the user triggers the download (browser has no completion
  // event for anchor downloads); swaps the toast to a "downloaded" success
  // state and hides the Download button.
  const [downloaded, setDownloaded] = useState(false);
  // Per-file download state for a multi-file (transformed) export, keyed by index.
  const [downloadedFiles, setDownloadedFiles] = useState<readonly number[]>([]);

  const { exportId, toastId, label } = session;

  useExportStatusStream({
    exportId,
    onStatus: (dto) => {
      setStreamUnavailable(false);
      updateExport(exportId, dto);
    },
    onUnavailable: () => {
      setStreamUnavailable(true);
    },
  });

  const dto = session.latest;
  const status: ExportStatus = streamUnavailable ? 'FAILED' : (dto?.status ?? 'PENDING');
  const written = dto?.writtenProducts ?? 0;
  const expected = dto?.expectedProducts ?? 0;
  const percent = computePercent(
    status,
    written,
    expected,
    dto?.chunksWritten ?? 0,
    dto?.chunksTotal ?? 0,
  );
  const failureReason: string | null = streamUnavailable ? null : (dto?.failureReason ?? null);
  const isTerminal = isTerminalExportStatus(status);

  const handleCancel = useCallback(() => {
    cancelMutation.mutate(exportId);
  }, [cancelMutation, exportId]);

  const handleRetry = useCallback(() => {
    // Drop the failed session + its toast; the new export gets its own runner.
    const onRetried = {
      onSuccess: () => {
        toast.dismiss(toastId);
        endExport(exportId);
      },
    };
    // A catalog export retries through the catalog route with its exact body.
    if (session.catalogRequest) {
      catalogTriggerMutation.mutate(
        { request: session.catalogRequest, label: session.label },
        onRetried,
      );
      return;
    }
    const request: ExportTriggerRequest = {
      sourceWorkflowId: session.sourceWorkflowId,
      tenantContext: session.tenantContext,
      ...(session.filters ? { filters: session.filters } : {}),
      ...(session.template ? { template: session.template } : {}),
    };
    triggerMutation.mutate(
      {
        request,
        label: session.label,
        connector: session.connector,
        // A transformed export retries through integrations with the same pick.
        ...(session.layout ? { layout: session.layout } : {}),
      },
      onRetried,
    );
  }, [triggerMutation, catalogTriggerMutation, session, toastId, endExport, exportId]);

  const handleDownload = useCallback(() => {
    void download(exportId, dto?.downloadUrl ?? null).then((ok) => {
      if (ok) setDownloaded(true);
    });
  }, [download, exportId, dto?.downloadUrl]);

  // Every file a transformed export wrote (§7b `files[]`); one file keeps the single
  // Download, which signs that same first file.
  const statusFiles = useMemo(() => dto?.files ?? [], [dto?.files]);
  const toastFiles = useMemo(
    () =>
      statusFiles.map((f, index) => ({
        name: f.subfolder ? `${f.subfolder}/${f.fileName}` : f.fileName,
        downloaded: downloadedFiles.includes(index),
      })),
    [statusFiles, downloadedFiles],
  );

  const handleDownloadFile = useCallback(
    (index: number) => {
      const file = statusFiles[index];
      if (!file) return;
      void downloadFile(exportId, file).then((ok) => {
        if (!ok) return;
        setDownloadedFiles((prev) => (prev.includes(index) ? prev : [...prev, index]));
      });
    },
    [downloadFile, exportId, statusFiles],
  );
  // "Hibbett · Catalog XML v3" for a transformed export, so the ready toast names it.
  const layoutName = dto?.transformerName
    ? `${dto.transformerName}${dto.transformerVersion != null ? ` v${String(dto.transformerVersion)}` : ''}`
    : null;
  // The toast becomes "downloaded" once every file of a multi-file export was fetched.
  const isDownloaded =
    downloaded || (statusFiles.length > 1 && downloadedFiles.length === statusFiles.length);

  // Re-emit the toast whenever the rendered state changes. Terminal toasts get
  // a close button and clear the session on dismiss; running toasts are
  // dismiss-locked (they close on completion, not by the user).
  useEffect(() => {
    toast(
      <ExportProgressToast
        label={label}
        status={status}
        percent={percent}
        writtenProducts={written}
        expectedProducts={expected}
        failureReason={failureReason}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onDownload={handleDownload}
        isCancelling={cancelMutation.isPending}
        isRetrying={triggerMutation.isPending || catalogTriggerMutation.isPending}
        isDownloading={isRefreshing}
        downloaded={isDownloaded}
        files={toastFiles}
        onDownloadFile={handleDownloadFile}
        layoutName={layoutName}
      />,
      isTerminal
        ? {
            id: toastId,
            duration: Infinity,
            closeButton: true,
            // `flex-1` stretches Sonner's content wrapper to the toast's full
            // width — without it the wrapper shrinks to its widest text line,
            // leaving the progress bar and actions floating in empty space.
            classNames: { content: 'flex-1 min-w-0' },
            onDismiss: () => {
              endExport(exportId);
            },
            onAutoClose: () => {
              endExport(exportId);
            },
          }
        : {
            id: toastId,
            duration: Infinity,
            closeButton: false,
            classNames: { content: 'flex-1 min-w-0' },
          },
    );
  }, [
    label,
    status,
    percent,
    written,
    expected,
    failureReason,
    isTerminal,
    toastId,
    exportId,
    endExport,
    handleCancel,
    handleRetry,
    handleDownload,
    cancelMutation.isPending,
    triggerMutation.isPending,
    catalogTriggerMutation.isPending,
    isRefreshing,
    isDownloaded,
    toastFiles,
    handleDownloadFile,
    layoutName,
  ]);

  return null;
}
